import { COMPLIANCE_CONFIG, COOKIE_CONSENT_STORAGE_KEY, type AnalyticsConsent } from '../config/compliance';

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

const GA_SCRIPT_ID = 'myoan-ga4-script';
const CLARITY_SCRIPT_ID = 'myoan-clarity-script';

let gaInitialized = false;
let clarityInitialized = false;

export type AnalyticsEventName =
  | 'start_reading_clicked'
  | 'spread_selected'
  | 'theme_question_selected'
  | 'card_selection_started'
  | 'cards_selected'
  | 'payment_checkout_opened'
  | 'payment_started'
  | 'payment_success'
  | 'reading_result_viewed'
  | 'summary_viewed'
  | 'wallet_recharge_opened'
  | 'login_started'
  | 'login_blocked_in_app_browser';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

function hasAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    return (window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) as AnalyticsConsent | null) === 'granted';
  } catch {
    return false;
  }
}

function injectScript(id: string, src: string): void {
  if (document.getElementById(id)) return;

  const script = document.createElement('script');
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function initGa4(measurementId: string): void {
  if (!measurementId || gaInitialized) return;

  injectScript(GA_SCRIPT_ID, `https://www.googletagmanager.com/gtag/js?id=${measurementId}`);

  window.dataLayer = window.dataLayer || [];
  const gtag = (...args: unknown[]) => {
    window.dataLayer?.push(args);
  };

  window.gtag = window.gtag || gtag;
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    send_page_view: false,
  });

  gaInitialized = true;
}

function initClarity(projectId: string): void {
  if (!projectId || clarityInitialized) return;

  if (typeof window.clarity !== 'function') {
    const queuedCalls: unknown[][] = [];
    const clarityFn = (...args: unknown[]) => {
      queuedCalls.push(args);
    };
    window.clarity = clarityFn;
  }

  injectScript(CLARITY_SCRIPT_ID, `https://www.clarity.ms/tag/${projectId}`);
  window.clarity?.('consent', true);
  clarityInitialized = true;
}

export function enableAnalytics(): void {
  const gaId = COMPLIANCE_CONFIG.gaMeasurementId;
  const clarityId = COMPLIANCE_CONFIG.clarityProjectId;
  let providerEnabled = false;

  if (gaId) {
    const gaDisableKey = `ga-disable-${gaId}` as const;
    (window as unknown as Record<string, unknown>)[gaDisableKey] = false;
    initGa4(gaId);
    providerEnabled = true;
  }

  if (clarityId) {
    initClarity(clarityId);
    providerEnabled = true;
  }

  if (providerEnabled) {
    trackPageView(`${window.location.pathname}${window.location.search}`, document.title);
  }
}

export function disableAnalytics(): void {
  const gaId = COMPLIANCE_CONFIG.gaMeasurementId;
  if (gaId) {
    const gaDisableKey = `ga-disable-${gaId}` as const;
    (window as unknown as Record<string, unknown>)[gaDisableKey] = true;
  }

  if (typeof window.clarity === 'function') {
    window.clarity('consent', false);
  }
}

export function trackPageView(path: string, title = document.title): void {
  if (!hasAnalyticsConsent()) return;

  const gaId = COMPLIANCE_CONFIG.gaMeasurementId;
  if (gaId && typeof window.gtag === 'function') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_location: window.location.href,
      page_title: title,
    });
  }

  if (typeof window.clarity === 'function') {
    window.clarity('event', 'page_view');
  }
}

export function trackEvent(name: AnalyticsEventName, params: AnalyticsParams = {}): void {
  if (!hasAnalyticsConsent()) return;

  if (typeof window.gtag === 'function') {
    window.gtag('event', name, params);
  }

  if (typeof window.clarity === 'function') {
    window.clarity('event', name);
  }
}
